import React, { useMemo, useCallback, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Menu,
    MenuItem
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    LocationOn as LocationIcon,
    AccountTree as AccountTreeIcon,
    History as HistoryIcon,
    AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { TableVirtuoso } from 'react-virtuoso';
import { marked } from 'marked';
import { statusDefs, allowedTransitions, PersonCustomIcon, CreatedDateIcon, DeadlineDateIcon, EditedDateIcon } from '../TasksView';

// Настройка marked для обработки markdown
marked.setOptions({
    breaks: true,
    gfm: true,
    sanitize: false,
    smartLists: true,
    smartypants: false,
});



// Функция для безопасной обработки markdown (упрощенная версия для таблицы)
const processMarkdownForTable = (text) => {
    if (!text) return '';

    try {
        // Для таблицы используем упрощенную обработку без некоторых элементов
        const html = marked(text);
        // Удаляем параграфы для более компактного отображения
        return html.replace(/<\/?p>/g, '');
    } catch (error) {
        console.warn('Markdown parsing error:', error);
        return text.replace(/\n/g, '<br>');
    }
};

// Мемоизированный компонент строки таблицы
const TableRowComponent = React.memo(({
    task,
    darkMode,
    users,
    canDragTask,
    canEditTask,
    canDeleteTask,
    handleStatusChange,
    handleEditTask,
    handleDeleteTask,
    handleViewSpecs,
    handleViewFiles,
    handleFocusOnTaskArea,
    handleOpenHistoryDialog,
    formatDate
}) => {
    // Общие стили для ячеек данных
    const dataCellStyle = useMemo(() => ({
        padding: { xs: '0.25rem', md: '0.5rem' },
        borderBottom: darkMode
            ? '1px solid rgba(255, 255, 255, 0.06)'
            : '1px solid rgba(0, 0, 0, 0.06)',
        color: darkMode ? '#f5f7fa' : '#333',
        fontSize: { xs: '0.75rem', md: '0.875rem' }
    }), [darkMode]);

    // Мемоизируем обработку описания
    const processedDescription = useMemo(() => {
        const description = task.description || task.desc;
        return processMarkdownForTable(description);
    }, [task.description, task.desc]);

    // Мемоизированные обработчики
    const handleEditClick = useCallback(() => {
        handleEditTask(task);
    }, [task, handleEditTask]);

    const handleDeleteClick = useCallback(() => {
        handleDeleteTask(task.id);
    }, [task.id, handleDeleteTask]);

    const handleViewSpecsClick = useCallback(() => {
        handleViewSpecs(task);
    }, [task, handleViewSpecs]);

    const handleViewFilesClick = useCallback(() => {
        handleViewFiles(task);
    }, [task, handleViewFiles]);

    const handleFocusAreaClick = useCallback(() => {
        handleFocusOnTaskArea(task);
    }, [task, handleFocusOnTaskArea]);

    const handleHistoryClick = useCallback(() => {
        handleOpenHistoryDialog(task);
    }, [task, handleOpenHistoryDialog]);

    const isOverdue = useMemo(() => {
        return task.deadline && new Date(task.deadline) < new Date() && task.status !== 'done' && task.status !== 'cancelled';
    }, [task.deadline, task.status]);

    return (
        <TableRow
            sx={{
                '&:hover': {
                    bgcolor: darkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'
                },
                bgcolor: darkMode ? '#2b2b2b' : '#fff'
            }}
        >
            {/* ID */}
            <TableCell align='center' sx={{
                ...dataCellStyle,
                fontWeight: 600,
                color: darkMode ? '#4fc3f7' : '#0288d1',
                width: { xs: '50px', md: '80px' },
                fontSize: { xs: '0.7rem', md: '0.875rem' }
            }}>
                {task.id}
            </TableCell>

            {/* Название */}
            <TableCell sx={{
                ...dataCellStyle,
                fontWeight: 600,
                minWidth: { xs: '120px', md: '150px' },
                maxWidth: { xs: '150px', md: '200px' },
                wordBreak: 'break-word'
            }}>
                {task.title || task.name}
            </TableCell>

            {/* Описание */}
            <TableCell sx={{
                ...dataCellStyle,
                color: darkMode ? '#bbb' : '#555',
                maxWidth: { xs: '100px', md: '200px' },
                display: { xs: 'none', md: 'table-cell' }
            }}>
                {processedDescription ? (
                    <Tooltip
                        title={
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    p: 0.5,
                                    lineHeight: 1.2,
                                    '& p': { margin: 0 },
                                    '& ul, & ol': { margin: '0.25rem 0', paddingLeft: 1.5 },
                                    '& li': { marginBottom: '0.1rem' }
                                }}
                                dangerouslySetInnerHTML={{ __html: processedDescription }}
                            />
                        }
                        arrow
                        placement="top"
                    >
                        <Box
                            sx={{
                                fontSize: '0.75rem',
                                lineHeight: 1.4,
                                maxHeight: '60px',
                                overflow: 'hidden',
                                display: '-webkit-box',
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: 'vertical',
                                cursor: 'help',
                                '& p': { margin: 0 }
                            }}
                            dangerouslySetInnerHTML={{ __html: processedDescription }}
                        />
                    </Tooltip>
                ) : '-'}
            </TableCell>

            {/* Статус */}
            <TableCell sx={{
                ...dataCellStyle,
                minWidth: { xs: '80px', md: '120px' }
            }}>
                <Box sx={{
                    display: 'flex',
                    gap: { xs: 0.5, md: 1 },
                    alignItems: 'center',
                    flexDirection: { xs: 'column', sm: 'column' },
                    justifyContent: 'center'
                }}>
                    {canDragTask(task) ? (
                        <StatusChip
                            task={task}
                            darkMode={darkMode}
                            onStatusChange={(newStatus) => handleStatusChange(task, newStatus)}
                        />
                    ) : (
                        <Chip
                            label={task.statusTitle}
                            sx={{
                                backgroundColor: task.statusColor,
                                color: 'white',
                                fontSize: { xs: '0.65rem', md: '0.75rem' },
                                fontWeight: 600,
                                height: { xs: 20, md: 24 }
                            }}
                        />
                    )}
                    {/* Индикация просроченных задач */}
                    {task.deadline &&
                        new Date(task.deadline) < new Date() &&
                        task.status !== 'done' &&
                        task.status !== 'cancelled' && (
                            <Chip
                                label={window.innerWidth < 600 ? "ПРОС." : "ПРОСРОЧЕНО"}
                                size="small"
                                sx={{
                                    height: { xs: 18, md: 20 },
                                    fontSize: { xs: '0.6rem', md: '0.65rem' },
                                    bgcolor: 'rgba(244, 67, 54, 0.1)',
                                    color: '#f44336',
                                    border: '1px solid rgba(244, 67, 54, 0.5)',
                                    fontWeight: 700
                                }}
                            />
                        )}
                </Box>
            </TableCell>

            {/* Постановщик */}
            <TableCell align='center' sx={{
                ...dataCellStyle,
                minWidth: { xs: '80px', md: '100px' },
                display: { xs: 'none', sm: 'table-cell' }
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                    <Chip
                        icon={<PersonCustomIcon color={darkMode ? '#98DB51' : '#7CB342'} sx={{ fontSize: '14px' }} />}
                        label={users.get(task.creator) || '-'}
                        size="small"
                        sx={{
                            backgroundColor: darkMode ? 'rgba(124, 179, 66, 0.15)' : 'rgba(124, 179, 66, 0.1)',
                            color: darkMode ? '#98DB51' : '#7CB342',
                            border: '1px solid rgba(124, 179, 66, 0.3)',
                            '& .MuiChip-icon': {
                                color: darkMode ? '#98DB51' : '#7CB342'
                            },
                            fontSize: '0.7rem',
                            height: '20px'
                        }}
                    />
                    <Chip
                        icon={<CreatedDateIcon color={darkMode ? '#98DB51' : '#7CB342'} sx={{ fontSize: '14px' }} />}
                        label={task.created_at ? formatDate(new Date(task.created_at)) : task.created || '-'}
                        size="small"
                        sx={{
                            backgroundColor: darkMode ? 'rgba(124, 179, 66, 0.15)' : 'rgba(124, 179, 66, 0.1)',
                            color: darkMode ? '#98DB51' : '#7CB342',
                            border: '1px solid rgba(124, 179, 66, 0.3)',
                            '& .MuiChip-icon': {
                                color: darkMode ? '#98DB51' : '#7CB342'
                            },
                            fontSize: '0.7rem',
                            height: '20px'
                        }}
                    />
                </Box>
            </TableCell>

            {/* Создано */}
            {/* <TableCell sx={{
                ...dataCellStyle,
                color: darkMode ? '#bbb' : '#555',
                minWidth: { xs: '80px', md: '100px' },
                display: { xs: 'none', sm: 'table-cell' }
            }}>
                {task.created_at ? formatDate(new Date(task.created_at)) : task.created}
            </TableCell> */}

            {/* Ответственный */}
            <TableCell align='center' sx={{
                ...dataCellStyle,
                minWidth: { xs: '80px', md: '100px' },
                maxWidth: { xs: '100px', md: '120px' },
                wordBreak: 'break-word'
            }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                    <Chip
                        icon={<PersonCustomIcon color={isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2'} sx={{ fontSize: '14px' }} />}
                        label={users.get(task.responsible) || '-'}
                        size="small"
                        sx={{
                            backgroundColor: isOverdue
                                ? (darkMode ? 'rgba(220, 20, 60, 0.15)' : 'rgba(220, 20, 60, 0.1)')
                                : (darkMode ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.1)'),
                            color: isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2',
                            border: `1px solid ${isOverdue ? 'rgba(220, 20, 60, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                            '& .MuiChip-icon': {
                                color: isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2'
                            },
                            fontSize: '0.7rem',
                            height: '20px'
                        }}
                    />
                    <Chip
                        icon={<DeadlineDateIcon color={isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2'} sx={{ fontSize: '14px' }} />}
                        label={task.deadline ? formatDate(new Date(task.deadline)) : '-'}
                        size="small"
                        sx={{
                            backgroundColor: isOverdue
                                ? (darkMode ? 'rgba(220, 20, 60, 0.15)' : 'rgba(220, 20, 60, 0.1)')
                                : (darkMode ? 'rgba(25, 118, 210, 0.15)' : 'rgba(25, 118, 210, 0.1)'),
                            color: isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2',
                            border: `1px solid ${isOverdue ? 'rgba(220, 20, 60, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                            '& .MuiChip-icon': {
                                color: isOverdue ? darkMode ? '#F51643' : '#DC143C' : darkMode ? '#1E8FFF' : '#1976D2'
                            },
                            fontSize: '0.7rem',
                            height: '20px'
                        }}
                    />
                    {/* Чип для соисполнителей (показывается только если есть соисполнители) */}
                    {task.coexecutors && task.coexecutors.length > 0 && (
                        task.coexecutors.map(c => (
                            <Chip
                                key={c}
                                icon={<PersonCustomIcon color={darkMode ? '#CB33E6' : '#9C27B0'} sx={{ fontSize: '14px' }} />}
                                label={users.get(c)}
                                size="small"
                                sx={{
                                    backgroundColor: darkMode ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.1)',
                                    color: darkMode ? '#CB33E6' : '#9C27B0',
                                    border: '1px solid rgba(156, 39, 176, 0.3)',
                                    '& .MuiChip-icon': {
                                        color: darkMode ? '#CB33E6' : '#9C27B0'
                                    },
                                    fontSize: '0.7rem',
                                    height: '20px'
                                }}
                            />
                        ))

                    )}
                </Box>
            </TableCell>

            {/* Дедлайн */}
            {/* <TableCell sx={{
                ...dataCellStyle,
                color: darkMode ? '#bbb' : '#555',
                    </Typography>
                    <Typography variant="caption">
                        {task.deadline ? formatDate(new Date(task.deadline)) : '-'}
                    </Typography>
                </Box>
            </TableCell>

            {/* Дедлайн */}
            {/* <TableCell sx={{
                ...dataCellStyle,
                color: darkMode ? '#bbb' : '#555',
                minWidth: { xs: '80px', md: '100px' }
            }}>
                {task.deadline ? formatDate(new Date(task.deadline)) : '-'}
            </TableCell> */}

            {/* Редактор */}
            <TableCell align='center' sx={{
                ...dataCellStyle,
                minWidth: { xs: '80px', md: '100px' },
                display: { xs: 'none', lg: 'table-cell' }
            }}>
                {task.editor && task.edited_at ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                        <Chip
                            icon={<PersonCustomIcon color={darkMode ? '#7ED1D4' : '#5F9EA0'} sx={{ fontSize: '14px' }} />}
                            label={users.get(task.editor) || ''}
                            size="small"
                            sx={{
                                backgroundColor: darkMode ? 'rgba(95, 158, 160, 0.15)' : 'rgba(95, 158, 160, 0.1)',
                                color: darkMode ? '#7ED1D4' : '#5F9EA0',
                                border: '1px solid rgba(95, 158, 160, 0.3)',
                                '& .MuiChip-icon': {
                                    color: darkMode ? '#7ED1D4' : '#5F9EA0'
                                },
                                fontSize: '0.7rem',
                                height: '20px'
                            }}
                        />
                        <Chip
                            icon={<EditedDateIcon color={darkMode ? '#7ED1D4' : '#5F9EA0'} sx={{ fontSize: '14px' }} />}
                            label={formatDate(new Date(task.edited_at))}
                            size="small"
                            sx={{
                                backgroundColor: darkMode ? 'rgba(95, 158, 160, 0.15)' : 'rgba(95, 158, 160, 0.1)',
                                color: darkMode ? '#7ED1D4' : '#5F9EA0',
                                border: '1px solid rgba(95, 158, 160, 0.3)',
                                '& .MuiChip-icon': {
                                    color: darkMode ? '#7ED1D4' : '#5F9EA0'
                                },
                                fontSize: '0.7rem',
                                height: '20px'
                            }}
                        />
                    </Box>
                ) : '-'}
            </TableCell>


            {/* Изменено */}
            {/* <TableCell sx={{
                ...dataCellStyle,
                color: darkMode ? '#bbb' : '#555',
                minWidth: { xs: '80px', md: '100px' },
                display: { xs: 'none', lg: 'table-cell' }
            }}>
                {task.edited_at ? formatDate(new Date(task.edited_at)) : '-'}
            </TableCell> */}

            {/* Действия */}
            <TableCell sx={{
                ...dataCellStyle,
                minWidth: { xs: '80px', md: '100px' }
            }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: { xs: 0.25, md: 0.5 },
                    flexWrap: 'wrap'
                }}>
                    {/* Кнопка показать на карте */}
                    {task.type === 'contour' && task.data?.objects?.pillars && task.data?.objects?.pillars.length > 0 && (
                        <IconButton
                            size="small"
                            onClick={handleFocusAreaClick}
                            sx={{
                                color: darkMode ? '#4fc3f7' : '#0277bd',
                                '&:hover': {
                                    bgcolor: darkMode
                                        ? 'rgba(79, 195, 247, 0.08)'
                                        : 'rgba(2, 119, 189, 0.04)',
                                    color: darkMode ? '#29b6f6' : '#0277bd'
                                },
                                p: { xs: 0.25, md: 0.5 }
                            }}
                            title="Показать на карте"
                        >
                            <LocationIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />
                        </IconButton>
                    )}

                    {/* Кнопка спецификаций */}
                    {task.type === 'contour' && Object.keys(task.data.objects).some(type => task.data.objects[type]?.length > 0) && (
                        <IconButton
                            size="small"
                            onClick={handleViewSpecsClick}
                            sx={{
                                color: darkMode ? '#ce93d8' : '#9c27b0',
                                '&:hover': {
                                    bgcolor: darkMode
                                        ? 'rgba(156, 39, 176, 0.08)'
                                        : 'rgba(156, 39, 176, 0.04)',
                                    color: darkMode ? '#ba68c8' : '#8e24aa'
                                },
                                p: { xs: 0.25, md: 0.5 }
                            }}
                        >
                            <AccountTreeIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />
                        </IconButton>
                    )}

                    {/* Кнопка просмотра файлов */}
                    {/* {task.attachments && task.attachments.length > 0 && ( */}
                    <IconButton
                        size="small"
                        onClick={handleViewFilesClick}
                        sx={{
                            color: darkMode ? '#81c784' : '#4caf50',
                            '&:hover': {
                                bgcolor: darkMode
                                    ? 'rgba(76, 175, 80, 0.08)'
                                    : 'rgba(76, 175, 80, 0.04)',
                                color: darkMode ? '#a5d6a7' : '#66bb6a'
                            },
                            p: { xs: 0.25, md: 0.5 }
                        }}
                    >
                        <AttachFileIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />
                    </IconButton>
                    {/* )} */}

                    {/* Кнопка редактирования */}
                    {canEditTask(task) ? (
                        <IconButton
                            size="small"
                            onClick={handleEditClick}
                            sx={{
                                color: darkMode ? '#f5f7fa' : '#333',
                                '&:hover': {
                                    bgcolor: darkMode
                                        ? 'rgba(25, 118, 210, 0.08)'
                                        : 'rgba(25, 118, 210, 0.04)',
                                    color: '#1976d2'
                                },
                                p: { xs: 0.25, md: 0.5 }
                            }}
                        >
                            <EditIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />
                        </IconButton>
                    ) : (
                        <Typography variant="caption" sx={{
                            color: darkMode ? '#666' : '#999',
                            fontStyle: 'italic',
                            fontSize: { xs: '0.65rem', md: '0.75rem' },
                            display: { xs: 'none', sm: 'inline' }
                        }}>
                            Нет прав
                        </Typography>
                    )}



                    {/* Кнопка истории изменений */}
                    <IconButton
                        size="small"
                        onClick={handleHistoryClick}
                        sx={{
                            color: darkMode ? '#ff9800' : '#f57c00',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255, 152, 0, 0.1)' : 'rgba(245, 124, 0, 0.1)',
                            },
                            p: { xs: 0.25, md: 0.5 }
                        }}
                    >
                        <HistoryIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />
                    </IconButton>

                    {/* Кнопка удаления */}
                    {canDeleteTask(task) && (
                        <IconButton
                            size="small"
                            onClick={handleDeleteClick}
                            sx={{
                                color: '#f44336',
                                '&:hover': {
                                    bgcolor: 'rgba(244, 67, 54, 0.08)'
                                },
                                p: { xs: 0.25, md: 0.5 }
                            }}
                        >
                            <DeleteIcon sx={{ fontSize: { xs: '16px', md: '18px' } }} />
                        </IconButton>
                    )}
                </Box>
            </TableCell>
        </TableRow >
    );
});

// Компонент статуса с выпадающим меню для изменения
const StatusChip = React.memo(({ task, darkMode, onStatusChange }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = useCallback((event) => {
        event.stopPropagation(); // Предотвращаем всплытие события
        setAnchorEl(event.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleStatusChangeClick = useCallback((newStatus) => {
        // Проверяем разрешенные переходы
        const currentStatus = task.status;
        if (allowedTransitions[currentStatus]?.includes(newStatus) && newStatus !== currentStatus) {
            onStatusChange(newStatus);
        }
        handleClose();
    }, [task.status, onStatusChange, handleClose]);

    const currentStatusDef = useMemo(() =>
        statusDefs.find(s => s.id === task.status),
        [task.status]
    );

    const availableStatuses = useMemo(() =>
        statusDefs.filter(status =>
            allowedTransitions[task.status]?.includes(status.id) && status.id !== task.status
        ),
        [task.status]
    );

    return (
        <>
            <Chip
                label={task.statusTitle || currentStatusDef?.title}
                onClick={handleClick}
                sx={{
                    pr: '0.5rem',
                    backgroundColor: task.statusColor || currentStatusDef?.color,
                    color: 'white',
                    fontSize: { xs: '0.65rem', md: '0.75rem' },
                    fontWeight: 600,
                    height: { xs: 20, md: 24 },
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '& .MuiChip-label': {
                        pr: '0rem',
                    },
                    '&:hover': {
                        backgroundColor: task.statusColor || currentStatusDef?.color,
                        opacity: 0.8,
                        transform: 'scale(1.05)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                    },
                    '&::after': {
                        content: '" ▼"',
                        fontSize: '0.6rem',
                        marginLeft: '4px',
                        opacity: 0.7
                    }
                }}
            />
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'center', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                sx={{
                    '& .MuiPaper-root': {
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                        borderRadius: '8px',
                        boxShadow: darkMode
                            ? '0 8px 32px rgba(0,0,0,0.4)'
                            : '0 8px 32px rgba(0,0,0,0.15)',
                        minWidth: '160px'
                    },
                    '& .MuiList-root': {
                        py: 0
                    },
                    zIndex: 2000
                }}
            >
                {/* Заголовок меню */}
                <Box sx={{
                    px: 2,
                    py: 0.5,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderBottom: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.1)',
                    bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                }}>
                    <Typography variant="caption" sx={{
                        color: darkMode ? '#bbb' : '#666',
                        fontWeight: 600,
                        fontSize: '0.7rem'
                    }}>
                        Изменить статус
                    </Typography>
                </Box>

                {/* Текущий статус */}
                {/* <MenuItem disabled sx={{ opacity: 1, py: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                        <Chip
                            label={currentStatusDef?.title}
                            size="small"
                            sx={{
                                backgroundColor: currentStatusDef?.color,
                                color: 'white',
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                mr: 1,
                                minWidth: '90px',
                                height: '20px'
                            }}
                        />
                    </Box>
                </MenuItem> */}

                {/* Доступные статусы */}
                {availableStatuses.length > 0 ? (
                    availableStatuses.map((status) => (
                        <MenuItem
                            key={status.id}
                            onClick={() => handleStatusChangeClick(status.id)}
                            sx={{
                                py: 1,
                                '&:hover': {
                                    bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                                <Chip
                                    label={status.title}
                                    size="small"
                                    sx={{
                                        backgroundColor: status.color,
                                        color: 'white',
                                        fontSize: '0.65rem',
                                        fontWeight: 600,
                                        minWidth: '90px',
                                        height: '20px'
                                    }}
                                />
                            </Box>
                        </MenuItem>
                    ))
                ) : (
                    <MenuItem disabled sx={{ py: 1 }}>
                        <Typography variant="caption" sx={{
                            color: darkMode ? '#666' : '#999',
                            fontStyle: 'italic',
                            fontSize: '0.7rem'
                        }}>
                            Нет доступных переходов
                        </Typography>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
});

// Компонент заголовка таблицы
const TableHeader = React.memo(({ darkMode }) => {
    const headerCellStyle = useMemo(() => ({
        background: darkMode ? '#1e1e1e' : 'rgba(248, 249, 250, 0.8)',
        fontWeight: 600,
        fontSize: { xs: '0.75rem', md: '0.875rem' },
        color: darkMode ? '#f5f7fa' : '#374151',
        borderBottom: darkMode
            ? '2px solid rgba(255, 255, 255, 0.12)'
            : '2px solid rgba(0, 0, 0, 0.12)',
        position: 'sticky',
        top: 0,
        zIndex: 1,
        py: { xs: 0.5, md: 1 },
        px: { xs: 0.5, md: 1 }
    }), [darkMode]);

    return (
        <TableHead>
            <TableRow>
                <TableCell align='center' sx={{
                    ...headerCellStyle,
                    width: { xs: '50px', md: '80px' },
                    minWidth: { xs: '50px', md: '80px' }
                }}>ID</TableCell>
                <TableCell align='center' sx={{
                    ...headerCellStyle,
                    minWidth: { xs: '120px', md: '150px' }
                }}>Название</TableCell>
                <TableCell align='center' sx={{
                    ...headerCellStyle,
                    minWidth: { xs: '100px', md: '150px' },
                    display: { xs: 'none', md: 'table-cell' }
                }}>Описание</TableCell>
                <TableCell align='center' sx={{
                    ...headerCellStyle,
                    minWidth: { xs: '80px', md: '120px' }
                }}>Статус</TableCell>
                <TableCell align='center' sx={{
                    ...headerCellStyle,
                    minWidth: { xs: '80px', md: '100px' },
                    display: { xs: 'none', sm: 'table-cell' }
                }}>Постановщик</TableCell>
                {/* <TableCell align='center' sx={{
                    ...headerCellStyle,
                    minWidth: { xs: '80px', md: '100px' },
                    display: { xs: 'none', sm: 'table-cell' }
                }}>Создано</TableCell> */}
                <TableCell align='center' sx={{
                    ...headerCellStyle,
                    minWidth: { xs: '80px', md: '100px' }
                }}>Ответственный</TableCell>
                {/* <TableCell align='center' sx={{
                    ...headerCellStyle,
                    minWidth: { xs: '80px', md: '100px' }
                }}>Дедлайн</TableCell> */}
                <TableCell align='center' sx={{
                    ...headerCellStyle,
                    minWidth: { xs: '80px', md: '100px' },
                    display: { xs: 'none', lg: 'table-cell' }
                }}>Редактор</TableCell>
                {/* <TableCell align='center' sx={{
                    ...headerCellStyle,
                    minWidth: { xs: '80px', md: '100px' },
                    display: { xs: 'none', lg: 'table-cell' }
                }}>Изменено</TableCell> */}
                <TableCell align='center' sx={{
                    ...headerCellStyle,
                    width: { xs: '80px', md: '100px' },
                    minWidth: { xs: '80px', md: '100px' }
                }}>Действия</TableCell>
            </TableRow>
        </TableHead>
    );
});

const TableView2 = ({
    darkMode,
    getAllTasks,
    users,
    canDragTask,
    canEditTask,
    canDeleteTask,
    handleStatusChange,
    handleEditTask,
    handleDeleteTask,
    handleViewSpecs,
    handleViewFiles,
    handleFocusOnTaskArea,
    handleOpenHistoryDialog,
    formatDate
}) => {
    const allTasks = useMemo(() => getAllTasks(), [getAllTasks]);

    // Компонент элемента виртуального списка
    const RowContent = useCallback((index) => (
        <TableRowComponent
            key={allTasks[index].id}
            task={allTasks[index]}
            darkMode={darkMode}
            users={users}
            canDragTask={canDragTask}
            canEditTask={canEditTask}
            canDeleteTask={canDeleteTask}
            handleStatusChange={handleStatusChange}
            handleEditTask={handleEditTask}
            handleDeleteTask={handleDeleteTask}
            handleViewSpecs={handleViewSpecs}
            handleViewFiles={handleViewFiles}
            handleFocusOnTaskArea={handleFocusOnTaskArea}
            handleOpenHistoryDialog={handleOpenHistoryDialog}
            formatDate={formatDate}
        />
    ), [allTasks, darkMode, canDragTask, canEditTask, canDeleteTask, handleStatusChange, handleEditTask, handleDeleteTask, handleViewSpecs, handleViewFiles, handleFocusOnTaskArea, handleOpenHistoryDialog, formatDate]);

    return (
        <Box sx={{
            height: '100%',
            width: '99%',
            p: { xs: 0.5, md: 2 }
        }}>
            <Paper
                sx={{
                    borderRadius: { xs: '4px', md: '8px' },
                    boxShadow: darkMode
                        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                        : '0 2px 8px rgba(0, 0, 0, 0.08)',
                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.05)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                {allTasks.length > 20 ? (
                    // Виртуализированная таблица для больших списков
                    <TableVirtuoso
                        style={{ height: '100%' }}
                        data={allTasks}
                        components={{
                            Scroller: React.forwardRef((props, ref) => (
                                <TableContainer component="div" {...props} ref={ref} sx={{ overflowX: 'auto' }} />
                            )),
                            Table: (props) => (
                                <Table {...props} sx={{
                                    minWidth: { xs: 800, md: 1200 },
                                    '& .MuiTableCell-root': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'
                                    }
                                }} stickyHeader />
                            ),
                            TableHead: () => <TableHeader darkMode={darkMode} />,
                            TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
                            TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
                        }}
                        fixedHeaderContent={() => <TableHeader darkMode={darkMode} />}
                        itemContent={(index) => RowContent(index)}
                    />
                ) : (
                    // Обычная таблица для небольших списков
                    <TableContainer sx={{ overflowX: 'auto', flex: 1 }}>
                        <Table sx={{
                            minWidth: { xs: 800, md: 1200 },
                            '& .MuiTableCell-root': {
                                borderColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'
                            }
                        }} stickyHeader>
                            <TableHeader darkMode={darkMode} />
                            <TableBody>
                                {allTasks.map((task, index) => RowContent(index))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default TableView2;
